import { defineApp } from '@colplat/schema';

/**
 * Users, profiles and per-app role grants (ADR-0018). Username/password/email
 * are deliberately absent here — those live hand-written in
 * `colplat_{env}_platform`, never through `field_history` (a password hash
 * must never be browsable in a history panel, and login-checking has to work
 * before any JWT exists). A platform `users.id` and the matching `user`
 * record here share the same UUIDv7, joined by `services/api`.
 */
export default defineApp({
  id: 'administration',
  schemaVersion: 1,

  entities: {
    user: {
      editMode: 'field',
      offline: 'full',
      projection: 'user',
      fields: {
        created:     { type: 'datetime', system: true, label: 'Created' },
        displayName: { type: 'string', max: 120, required: true, label: 'Display name' },
        active:      { type: 'bool', label: 'Active' },   // -> lease 'cas' by default
      },
    },

    roleGrant: {
      editMode: 'field',
      offline: 'full',
      projection: 'role_grant',
      parent: { entity: 'user', field: 'userId' },
      fields: {
        created:   { type: 'datetime', system: true, label: 'Created' },
        userId:    { type: 'uuid', required: true, indexed: true, label: 'User' },
        appId:     { type: 'string', max: 64, required: true, indexed: true, label: 'App' },
        role:      { type: 'string', max: 64, required: true, label: 'Role' },
        grantedBy: { type: 'uuid', required: true, label: 'Granted by' },
        // Revoking a role toggles this off — the row is never deleted, so the
        // grant's own history (who granted it, when it was revoked) stays visible.
        active:    { type: 'bool', label: 'Active' },
      },
      indexes: [['appId', 'role']],
    },
  },

  // Only a super-admin may write here; anyone else who holds SOME role on
  // this app can read the directory; zero roles on 'administration' means
  // hidden entirely, not a default 'read' — `ctx.roles` is already scoped to
  // this app (RightsContext is per-app), so an empty list here means "no
  // grant on administration at all," not "no grant on anything." See ADR-0014.
  rights: {
    resolve: (ctx) => {
      if (ctx.roles.length === 0) return 'hidden';
      return ctx.roles.includes('super-admin') ? 'write' : 'read';
    },
  },

  hooks: {
    /**
     * Refuses to deactivate the last remaining active super-admin grant —
     * without this, a super-admin could lock every admin out of the one app
     * that can ever create a grant again. `queryByField`, not `queryChildren`:
     * `appId` is a string, not a uuid parent/child relation.
     */
    beforeCommit: async (ctx, change) => {
      if (change.entity !== 'roleGrant' || change.field !== 'active') return;
      if (!(change.value.t === 'bool' && change.value.v === false)) return;

      const role = await ctx.read('roleGrant', change.recordId, 'role');
      if (!(role?.t === 'string' && role.v === 'super-admin')) return;

      const appId = await ctx.read('roleGrant', change.recordId, 'appId');
      if (!(appId?.t === 'string')) return;

      // The projection row for `change.recordId` still holds its PRE-commit
      // value here (beforeCommit runs before the field is written) — exclude
      // it explicitly rather than relying on its not-yet-applied `active`.
      const siblings = await ctx.queryByField('roleGrant', 'appId', appId);
      const stillActive = siblings.filter((row) =>
        row.id !== change.recordId &&
        row.fields['role']?.t === 'string' && row.fields['role'].v === 'super-admin' &&
        row.fields['active']?.t === 'bool' && row.fields['active'].v === true);

      if (stillActive.length === 0) {
        throw new Error('cannot deactivate the last super-admin role grant');
      }
    },
  },

  views: { default: 'generated' },

  // The only role this app itself needs today; more (e.g. a read-only
  // "user-manager") is a real future need, not built until asked for.
  roles: ['super-admin'],
});
